import { useEffect, useMemo, useState } from 'react';
import { Ban, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { workItemPageApi, workItemApi } from '../../services/api';
import { feature } from '../../services/telemetry';
import { itemRef } from '../../utils/itemRef';
import { CheckoutBadge, NotWorked, liveCheckout } from './CheckoutBadge';
import type { WorkItem } from '../../types';

/**
 * Record what blocks this item.
 *
 * `dependencies` means BLOCKED BY, and the field has been in the model and in
 * use for a long time — 394 of 2,000 sampled items carry one, and they already
 * drive the Kanban blocked badge and the Gantt arrows. What was missing was any
 * way to record one from the product: every dependency in the estate had to be
 * written through the API.
 *
 * Sibling ranking is why this matters. WSJF compares like with like — tasks
 * within a story, stories within a feature — so the ordering inside a parent is
 * the plan. A task that waits on a task under a DIFFERENT story is the case
 * that ordering cannot express, and this is where it gets said.
 */

interface Props {
  item: WorkItem;
  onChange: (field: string, value: any) => Promise<void>;
}

export default function DependencyEditor({ item, onChange }: Props) {
  const ids = useMemo(() => item.dependencies || [], [item.dependencies]);
  const [resolved, setResolved] = useState<Record<string, WorkItem>>({});
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WorkItem[]>([]);
  const [busy, setBusy] = useState(false);

  // Resolve ids to something a human recognises. A bare id tells nobody what
  // they are waiting for.
  useEffect(() => {
    let live = true;
    (async () => {
      const missing = ids.filter((id) => !resolved[id]);
      if (!missing.length) return;
      const found: Record<string, WorkItem> = {};
      await Promise.all(missing.map(async (id) => {
        try { found[id] = await workItemApi.get(id); } catch { /* deleted or unreadable */ }
      }));
      if (live) setResolved((prev) => ({ ...prev, ...found }));
    })();
    return () => { live = false; };
  }, [ids, resolved]);

  useEffect(() => {
    if (!adding) return;
    const term = query.trim();
    if (term.length < 2) { setResults([]); return; }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const page = await workItemPageApi.page({ search: term }, 20, 0);
        if (!live) return;
        // Never offer the item itself — the backend rejects a self-reference,
        // but being offered it at all reads as though it were sensible.
        setResults((page.items as WorkItem[]).filter(
          (r) => r._id !== item._id && !ids.includes(r._id)));
      } catch { if (live) setResults([]); }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [query, adding, item._id, ids]);

  const save = async (next: string[]) => {
    setBusy(true);
    try {
      await onChange('dependencies', next);
      feature('workitems.dependencies');
    } catch (err: any) {
      // The backend walks the graph and refuses a cycle. Say which rule was
      // hit, rather than "failed to update".
      toast.error(String(err?.message || '').includes('Circular')
        ? 'That would create a circular dependency — the two items would block each other.'
        : 'Could not save the dependency.');
    } finally {
      setBusy(false);
    }
  };

  const add = async (dep: WorkItem) => {
    setAdding(false); setQuery(''); setResults([]);
    await save([...ids, dep._id]);
  };

  const remove = async (id: string) => save(ids.filter((d) => d !== id));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1">
          <Ban size={10} /> Blocked by
        </span>
        <button
          onClick={() => setAdding((a) => !a)}
          disabled={busy}
          className="text-xs text-accent hover:underline flex items-center gap-0.5 disabled:opacity-50"
        >
          <Plus size={11} /> Add
        </button>
      </div>

      {ids.length === 0 && !adding && (
        <p className="text-xs text-gray-400">
          Nothing is blocking this. Add what it waits on — most often a task
          under a different story, which the ordering inside a parent cannot say.
        </p>
      )}

      {ids.map((id) => {
        const dep = resolved[id];
        const done = dep?.status === 'done';
        return (
          <div key={id} className="flex items-center gap-2 text-sm">
            <span className={`font-mono text-xs shrink-0 ${done ? 'text-green-600' : 'text-amber-600'}`}>
              {dep ? itemRef(dep) : id.split(':')[0]}
            </span>
            <span className="truncate flex-1 text-gray-700 dark:text-gray-300">
              {dep ? dep.title : <span className="text-gray-400">no longer readable</span>}
            </span>
            {done && <span className="text-[10px] text-green-600 shrink-0">cleared</span>}
            {dep && !done && (liveCheckout(dep.checkout) ? <CheckoutBadge checkout={dep.checkout} compact /> : <NotWorked />)}
            <button
              onClick={() => remove(id)}
              disabled={busy}
              className="text-gray-400 hover:text-red-500 shrink-0 disabled:opacity-50"
              title="Remove this dependency"
            >
              <X size={13} />
            </button>
          </div>
        );
      })}

      {adding && (
        <div className="space-y-1">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by key or title…"
            className="w-full px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
            {results.map((r) => (
              <button
                key={r._id}
                onClick={() => add(r)}
                className="w-full text-left px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
              >
                <span className="font-mono text-[11px] text-gray-400 shrink-0">{itemRef(r)}</span>
                <span className="text-xs truncate text-gray-700 dark:text-gray-300">{r.title}</span>
              </button>
            ))}
            {query.trim().length >= 2 && results.length === 0 && (
              <p className="px-2 py-1.5 text-xs text-gray-400">Nothing matched.</p>
            )}
            {query.trim().length < 2 && (
              <p className="px-2 py-1.5 text-xs text-gray-400">Type at least two characters.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
